import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Zap, Shield, Siren } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";

const UV_IMG = "https://customer-assets.emergentagent.com/job_pandemic-ready/artifacts/d6ltys32_UV%20lamp.jpg";
const ACTIVE_IMG = "https://static.prod-images.emergentagent.com/jobs/15657b70-52f9-4fed-be78-382892abee41/images/aefa105d429fe37b5ba3b80c149198dd9b6c2fbf004c1b89bd7ba3e7685f2bc5.png";
const INACTIVATED_IMG = "https://static.prod-images.emergentagent.com/jobs/15657b70-52f9-4fed-be78-382892abee41/images/fbf7ec820c71037d8b3b93858cc23b588cf89999f28a9cadb3cdbc9fb478259e.png";

const stageImages = { active: ACTIVE_IMG, uv: UV_IMG, inactivated: INACTIVATED_IMG };
const stageMeta = [
  { id: "active", color: "#EF4444", icon: Siren },
  { id: "uv", color: "#0044FF", icon: Zap },
  { id: "inactivated", color: "#10B981", icon: Shield },
];

export default function TechnologyExplorer() {
  const [activeStage, setActiveStage] = useState("uv");
  const { t } = useLanguage();
  const stageTitleKey = { active: "activeT", uv: "uvT", inactivated: "inT" };
  const stageDescKey  = { active: "activeD", uv: "uvD", inactivated: "inD" };
  const stages = stageMeta.map(s => ({
    ...s,
    title: t(`technology.stages.${stageTitleKey[s.id]}`),
    desc:  t(`technology.stages.${stageDescKey[s.id]}`),
  }));

  return (
    <section id="technology" data-testid="technology-section" className="py-24 md:py-32 bg-[#050505] relative overflow-hidden">
      {/* Subtle ambient glow */}
      <div className="absolute top-0 right-0 w-[400px] h-[400px] bg-[#EE1D25]/4 rounded-full blur-[200px]" />

      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative">
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="mb-16"
        >
          <p className="text-sm uppercase tracking-[0.2em] text-[#9CA3AF] font-mono mb-4">{t("technology.label")}</p>
          <h2 className="font-heading text-4xl md:text-5xl font-bold text-[#F3F4F6] tracking-tight">{t("technology.heading")}</h2>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-start">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="relative overflow-hidden rounded-lg"
          >
            <AnimatePresence mode="wait">
              <motion.img
                key={activeStage}
                src={stageImages[activeStage]}
                alt={stages.find(s => s.id === activeStage)?.title}
                initial={{ opacity: 0, scale: 1.02 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.98 }}
                transition={{ duration: 0.5 }}
                className={`w-full rounded-lg ${activeStage !== 'uv' ? 'mix-blend-lighten' : ''}`}
              />
            </AnimatePresence>
            <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-transparent rounded-lg" />
            {/* Stage color accent bar */}
            <div className="absolute top-0 left-0 right-0 h-1 flex">
              {stages.map(s => (
                <div
                  key={s.id}
                  className="flex-1 transition-opacity duration-500"
                  style={{ background: s.color, opacity: activeStage === s.id ? 1 : 0.15 }}
                />
              ))}
            </div>
            <div className="absolute bottom-6 left-6 right-6">
              <div className="flex gap-2">
                {stages.map(s => (
                  <button
                    key={s.id}
                    onClick={() => setActiveStage(s.id)}
                    className={`flex-1 h-1.5 rounded-full transition-all duration-300 ${activeStage === s.id ? 'opacity-100' : 'opacity-25'}`}
                    style={{ background: s.color }}
                    data-testid={`stage-indicator-${s.id}`}
                  />
                ))}
              </div>
            </div>
          </motion.div>

          <div className="space-y-3">
            {stages.map((stage, i) => (
              <motion.button
                key={stage.id}
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08 }}
                onClick={() => setActiveStage(stage.id)}
                data-testid={`stage-btn-${stage.id}`}
                className={`w-full text-left p-5 border transition-all duration-400 ${
                  activeStage === stage.id
                    ? "border-l-2 bg-[#0A0F1A]"
                    : "border-[#1F2937] hover:border-[#374151] hover:bg-[#0A0A0A]"
                }`}
                style={{ borderLeftColor: activeStage === stage.id ? stage.color : undefined }}
              >
                <div className="flex items-center gap-3 mb-1">
                  <div
                    className="w-8 h-8 flex items-center justify-center rounded-full transition-all duration-300"
                    style={{
                      background: activeStage === stage.id ? `${stage.color}20` : `${stage.color}08`,
                      boxShadow: activeStage === stage.id ? `0 0 12px ${stage.color}20` : 'none',
                    }}
                  >
                    <stage.icon size={16} style={{ color: stage.color }} />
                  </div>
                  <h4 className="font-heading text-base font-semibold text-[#F3F4F6]">{stage.title}</h4>
                </div>
                <AnimatePresence>
                  {activeStage === stage.id && (
                    <motion.p
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.3 }}
                      className="text-sm text-[#9CA3AF] mt-3 ml-11 leading-relaxed"
                    >
                      {stage.desc}
                    </motion.p>
                  )}
                </AnimatePresence>
              </motion.button>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
